import React, { useContext } from 'react';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import mq from 'src/styling/media-queries';
import type { Styling } from 'src/context/styling';
import type { CSSInterpolation } from '@emotion/serialize';
import { WebsiteLink } from 'src/components/common/website-link';
import { flex } from 'src/styling/constants';
import CountrySelection from 'src/components/layout/footer/redesign/country-selection';
import FooterCategory from 'src/components/layout/footer/redesign/footer-category';
import type { BuildType } from 'src/enums/build/build-type';
import { getFooterColors } from 'src/components/layout/footer/redesign/colors';
import { amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import { CopyrightInformation } from 'src/components/layout/footer/redesign/copyright-information';
import { SocialMediaSection } from 'src/components/layout/footer/redesign/social-media-section';
import { AppLinksSection } from 'src/components/layout/footer/redesign/app-links-section';
import { BrandLogo } from 'src/components/layout/footer/redesign/brand-logo';

const createComponentStyling = (styling: Styling, buildType: BuildType) => {
  const footerColors = getFooterColors(buildType);

  return {
    container: {
      backgroundColor: footerColors.backgroundColor,
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      color: footerColors.text,
    },
    footer: mq.withSmallTablet({
      maxWidth: '92.25rem',
      width: '100%',
      padding: ['2.5rem 1.25rem 4.5rem', '2.5rem 2.5rem', '2.5rem 3.75rem'],
      gap: [0, 0, '0.75rem'],
    }),
    desktopOnly: mq({
      display: ['none', 'unset'],
    }),
    topSectionWrapper: mq({
      gap: ['2.5rem', '1.5rem'],
      display: 'flex',
      flexDirection: ['column', 'row'],
      alignItems: 'center',
    }),
    footerSection: mq({
      flexBasis: ['100%', '20%'],
      flexGrow: ['initial', 1],
    }),
    logo: {
      padding: '2.5rem 0',
      position: 'relative',
    },
    mainContentWrapper: mq({
      borderBottom: '1px solid',
      borderBottomColor: footerColors.border,
      paddingBottom: ['4rem', '4.75rem'],
      paddingTop: ['3.25rem', 0],
      marginBottom: '1.5rem',
      gap: '1.5rem',
      display: 'flex',
      flexDirection: ['column', 'row'],
    }),
    sectionTitle: [
      styling.fonts.bold,
      mq({
        color: footerColors.heading,
        fontSize: '1rem',
        lineHeight: '125%',
        marginBottom: ['1rem', '2rem'],
      }),
    ],
  } satisfies Record<string, CSSInterpolation>;
};

const footerImageHeight = ['2.5rem', '2.25rem'];

const FooterRedesign = () => {
  const { newFooterContent } = useContext(GlobalCmsContent);
  const { styling, buildType, pageLevel } = useContext(SiteContext);

  if (!newFooterContent) {
    return;
  }

  const componentStyling = createComponentStyling(styling, buildType);

  const numberOfFooterNavMenuSections = newFooterContent.footerNavMenuSections?.length ?? 0;

  const logoAmplitudeAction = () =>
    amplitudeTrackEvent('Click to Logo - Footer', {
      custom_page: window.location.origin,
      custom_level: pageLevel,
    });

  const logoClick = () => {
    logoAmplitudeAction();
    document.body.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer css={componentStyling.container} id="footer">
      <div css={[componentStyling.footer, flex.column]}>
        <div css={componentStyling.topSectionWrapper}>
          <div css={[componentStyling.logo, componentStyling.footerSection]}>
            <WebsiteLink to="/" goToGlobal handleClick={logoClick}>
              <BrandLogo buildType={buildType} />
            </WebsiteLink>
          </div>
          {numberOfFooterNavMenuSections > 1 &&
            new Array(numberOfFooterNavMenuSections - 1)
              .fill(null)
              .map((_, index) => (
                <div
                  key={index}
                  css={[componentStyling.footerSection, componentStyling.desktopOnly]}
                />
              ))}
          {!!newFooterContent.footerAppLinks.images?.length && (
            <AppLinksSection
              title={newFooterContent.footerAppLinks.title}
              images={newFooterContent.footerAppLinks.images}
              footerImageHeight={footerImageHeight}
            />
          )}
        </div>
        <div css={componentStyling.mainContentWrapper}>
          {newFooterContent.footerNavMenuSections.map(({ title, links }) => (
            <div css={componentStyling.footerSection} key={title}>
              <FooterCategory title={title} links={links} />
            </div>
          ))}
          <div css={componentStyling.footerSection}>
            <div css={componentStyling.sectionTitle}>
              {newFooterContent.footerCountrySelection.title}
            </div>
            <CountrySelection
              currentCountryText={newFooterContent.footerCountrySelection.currentCountryText}
              links={newFooterContent.footerCountrySelection.links ?? []}
            />
            {!!newFooterContent.footerSocialMediaLinks.images?.length && (
              <SocialMediaSection
                title={newFooterContent.footerSocialMediaLinks.title}
                images={newFooterContent.footerSocialMediaLinks.images}
                footerImageHeight={footerImageHeight}
              />
            )}
          </div>
        </div>
        <CopyrightInformation
          copyrightInformation={newFooterContent.footerCopyrightInformation}
          imagesDisclaimer={newFooterContent.footerImagesDisclaimer}
        />
      </div>
    </footer>
  );
};

export default FooterRedesign;
